import React from 'react';

type IconProps = React.SVGProps<SVGSVGElement> & { size?: number | string };

const base = (size: number | string | undefined, className?: string) => ({
  width: size ?? 16,
  height: size ?? 16,
  className,
  'aria-hidden': true as const,
  focusable: false as const,
});

export const ArrowRight = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 20 20" fill="none" {...base(size, className)} {...rest}>
    <path d="M4 10h11m0 0-4-4m4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ArrowUpRight = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 20 20" fill="none" {...base(size, className)} {...rest}>
    <path d="M6 14 14 6m0 0H7m7 0v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Check = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 20 20" fill="none" {...base(size, className)} {...rest}>
    <path d="m5 10 3.5 3.5L15 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronDown = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 20 20" fill="none" {...base(size, className)} {...rest}>
    <path d="m5 8 5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronRight = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 20 20" fill="none" {...base(size, className)} {...rest}>
    <path d="m8 5 5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Close = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 20 20" fill="none" {...base(size, className)} {...rest}>
    <path d="M5 5l10 10M15 5 5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const Search = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 20 20" fill="none" {...base(size, className)} {...rest}>
    <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="2" />
    <path d="m13.5 13.5 3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const Phone = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...base(size, className)} {...rest}>
    <path d="M5 4h3.3a1 1 0 0 1 .95.68l1.5 4.5a1 1 0 0 1-.5 1.2l-2.26 1.13a11 11 0 0 0 5.52 5.52l1.13-2.26a1 1 0 0 1 1.2-.5l4.5 1.5a1 1 0 0 1 .68.95V20a2 2 0 0 1-2 2h-1C9.72 22 3 15.28 3 7V6a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

export const Mail = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...base(size, className)} {...rest}>
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const MapPin = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...base(size, className)} {...rest}>
    <path d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

export const Clock = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...base(size, className)} {...rest}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Download = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 20 20" fill="none" {...base(size, className)} {...rest}>
    <path d="M10 3v9m0 0 3.5-3.5M10 12 6.5 8.5M4 15h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Shield = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M12 3 4.5 6.4v5c0 4.4 3.2 8.5 7.5 9.6 4.3-1.1 7.5-5.2 7.5-9.6v-5L12 3Zm-1 12.1-3-3 1.4-1.4L11 12.3l4.6-4.6L17 9.1l-6 6Z" />
  </svg>
);

export const Droplet = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M12 2.5c-3.6 4.6-7 8.3-7 12.3a7 7 0 0 0 14 0c0-4-3.4-7.7-7-12.3Zm0 17.3a5 5 0 0 1-5-5c0-.4.3-.8.8-.8s.8.4.8.8a3.5 3.5 0 0 0 3.4 3.5c.5 0 .8.3.8.8s-.3.7-.8.7Z" />
  </svg>
);

export const Sparkle = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M12 2 14 8.5 20.5 10.5 14 12.5 12 19 10 12.5 3.5 10.5 10 8.5 12 2ZM19 16l1 2.5 2.5 1-2.5 1L19 23l-1-2.5-2.5-1 2.5-1L19 16Z" />
  </svg>
);

export const Brush = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M18 4V3a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6h1v4H9v11a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-9h8V4h-3Z" />
  </svg>
);

export const Palette = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M12 3a9 9 0 0 0 0 18c.8 0 1.5-.7 1.5-1.5 0-.4-.2-.8-.4-1-.3-.3-.4-.6-.4-1 0-.9.7-1.5 1.5-1.5H16a5 5 0 0 0 5-5c0-4.4-4-8-9-8Zm-5.5 9a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm3-4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm3.5 4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z" />
  </svg>
);

export const Calculator = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M17 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Zm-6 16H9v-2h2v2Zm0-4H9v-2h2v2Zm4 4h-2v-2h2v2Zm0-4h-2v-2h2v2Zm2-5H7V5h10v4Z" />
  </svg>
);

export const Users = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M16 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm-8 1a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm0 2c-2.3 0-6 1.2-6 3.5V20h7v-1.5c0-1 .4-2 1.2-2.8A9.6 9.6 0 0 0 8 14Zm8 0c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4Z" />
  </svg>
);

export const Star = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L1.5 7.7l5.9-.9L10 1.5z" />
  </svg>
);

export const Quote = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M9.6 6C6.5 6 4 8.5 4 11.6c0 3 2.3 5.4 5.2 5.4.5 0 1-.1 1.4-.2-.6 1.4-2 2.5-3.9 3l.9 2.2c4.2-1.2 7-4.8 7-9.8C14.6 8.2 12.4 6 9.6 6Zm9.8 0c-3.1 0-5.6 2.5-5.6 5.6 0 3 2.3 5.4 5.2 5.4.5 0 1-.1 1.4-.2-.6 1.4-2 2.5-3.9 3l.9 2.2c4.2-1.2 7-4.8 7-9.8C24.4 8.2 22.2 6 19.4 6Z" />
  </svg>
);

export const Play = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M8 5.5v13l11-6.5-11-6.5Z" />
  </svg>
);

export const Whatsapp = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.3.1-.2 0-.3 0-.5l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.8 12 12 0 0 0 4.6 4c1.7.7 2.3.8 3.2.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2l-.5-.4Z" />
  </svg>
);

export const Facebook = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M13.5 22v-8h2.7l.4-3.2h-3.1V8.8c0-.9.3-1.6 1.6-1.6h1.7V4.3c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2.3H7.3V14h2.8v8h3.4Z" />
  </svg>
);

export const Instagram = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...base(size, className)} {...rest}>
    <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="17.3" cy="6.7" r="1" fill="currentColor" />
  </svg>
);

export const Youtube = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M22.5 7.2a2.8 2.8 0 0 0-2-2C18.8 4.8 12 4.8 12 4.8s-6.8 0-8.5.4a2.8 2.8 0 0 0-2 2C1 8.9 1 12 1 12s0 3.1.5 4.8a2.8 2.8 0 0 0 2 2c1.7.4 8.5.4 8.5.4s6.8 0 8.5-.4a2.8 2.8 0 0 0 2-2c.5-1.7.5-4.8.5-4.8s0-3.1-.5-4.8ZM9.8 15V9l5.7 3-5.7 3Z" />
  </svg>
);

export const Linkedin = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...base(size, className)} {...rest}>
    <path d="M6.9 21H3.3V8.9h3.6V21ZM5.1 7.3a2.1 2.1 0 1 1 0-4.2 2.1 2.1 0 0 1 0 4.2ZM21 21h-3.6v-5.9c0-1.4 0-3.2-2-3.2s-2.2 1.5-2.2 3.1V21H9.6V8.9h3.4v1.7h.1c.5-.9 1.6-1.9 3.4-1.9 3.6 0 4.3 2.4 4.3 5.5V21Z" />
  </svg>
);

export const Sun = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...base(size, className)} {...rest}>
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
    <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M4.9 19.1l1.8-1.8M17.3 6.7l1.8-1.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const Layers = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...base(size, className)} {...rest}>
    <path d="m12 3 9 5-9 5-9-5 9-5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="m3 12 9 5 9-5M3 16l9 5 9-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Leaf = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...base(size, className)} {...rest}>
    <path d="M20 4c-9 0-15 4-15 12 0 1.5.3 2.8.8 4C9 14 13 10.5 18 9c-4.5 3-7.5 6.5-9 11 .9.2 1.9.3 3 .3 8 0 10-7.5 8-16.3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

export const Home = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...base(size, className)} {...rest}>
    <path d="m3 11 9-8 9 8v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1v-9Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

export const Menu = ({ size, className, ...rest }: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" {...base(size, className)} {...rest}>
    <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
